import { ICustomer, ITenantLocation } from "api/types";
import ApolloClient from "apollo-boost";
import gql from "graphql-tag";
import Hashes from "jshashes";

export const fetchAvailableCustomerInterests = async (
  apollo: ApolloClient<{}>
): Promise<{
  interests: any;
  usageTypes: any;
}> => {
  const result: any = await apollo.query({
    query: gql`
      query availableSegmentationDetails {
        availableSegmentationDetails {
          interests {
            id
            name
            image
          }
          usageTypes {
            id
            name
          }
        }
      }
    `
  });

  return {
    interests: result.data.availableSegmentationDetails.interests,
    usageTypes: result.data.availableSegmentationDetails.usageTypes
  };
};

export const getCustomerDetails = async (
  apollo: ApolloClient<{}>,
  authToken: string
): Promise<ICustomer> => {
  const result: any = await apollo.query({
    query: gql`
      query getCustomerByAuthToken($authToken: String!) {
        getCustomerByAuthToken(authToken: $authToken) {
          id
          phoneNumber
          pointsBalance
          interestIds
          usageTypeIds
        }
      }
    `,
    variables: {
      authToken
    },
    fetchPolicy: "network-only"
  });

  return result.data.getCustomerByAuthToken;
};

export const getCurrentLocation = async (
  apollo: ApolloClient<{}>
): Promise<ITenantLocation> => {
  const result: any = await apollo.query({
    query: gql`
      query currentLocation {
        me {
          ... on TenantTabletSelfInfo {
            location {
              name
              locationId
              tenantId
              vendorId
            }
          }
        }
      }
    `,
    fetchPolicy: "network-only"
  });

  return result.data.me.location;
};

export const updatePin = async (
  apollo: ApolloClient<{}>,
  authToken: string,
  newPin: string
): Promise<boolean> => {
  const result = await apollo.mutate({
    mutation: gql`
      mutation updatePin($authToken: String!, $newPin: String!) {
        updatePin(authToken: $authToken, newPin: $newPin)
      }
    `,
    variables: {
      authToken,
      newPin
    }
  });

  if (result.data && result.data.updatePin) {
    apollo.writeData({
      data: {
        loggedCustomer: {
          hashedPin: newPin ? new Hashes.MD5().hex(newPin) : "",
          __typename: "LoggedCustomer"
        }
      }
    });
  }

  return result.data ? result.data.updatePin : false;
};

export const updateCustomerInterestsAndUsageTypes = async (
  apollo: ApolloClient<{}>,
  authToken: string,
  newInterestIds: string[],
  newUsageTypeIds: string[]
): Promise<any> => {
  const result: any = await apollo.mutate({
    mutation: gql`
      mutation updateCustomerInterestsAndUsageTypes(
        $authToken: String!
        $newInterestIds: [String]!
        $newUsageTypeIds: [String]!
      ) {
        updateCustomerInterestsAndUsageTypes(
          authToken: $authToken
          newInterestIds: $newInterestIds
          newUsageTypeIds: $newUsageTypeIds
        )
      }
    `,
    variables: {
      authToken,
      newInterestIds,
      newUsageTypeIds
    }
  });

  return result.data.updateCustomerInterestsAndUsageTypes;
};

export const getRewards = async (
  apollo: ApolloClient<{}>,
  authToken: string
): Promise<any> => {
  const result: any = await apollo.query({
    query: gql`
      query availableRewards($authToken: String!) {
        availableRewards(authToken: $authToken) {
          id
          name
          pointsRequired
          imageId
        }
      }
    `,
    variables: {
      authToken
    },
    fetchPolicy: "network-only"
  });

  return result.data.availableRewards;
};

export const redeemPointsForReward = async (
  apollo: ApolloClient<{}>,
  authToken: string,
  tenantEmployeePin: string,
  rewardsAndQuantities: Array<{
    id: string;
    quantity: number;
  }>
): Promise<{
  pointsUsed: number;
  updatedCustomerPointBalance: number;
}> => {
  const result: any = await apollo.mutate({
    mutation: gql`
      mutation redeemPointsForReward(
        $authToken: String!
        $tenantEmployeePin: String!
        $rewardsAndQuantities: [RewardsAndQuantities]!
      ) {
        redeemPointsForReward(
          authToken: $authToken
          tenantEmployeePin: $tenantEmployeePin
          rewardsAndQuantities: $rewardsAndQuantities
        ) {
          pointsUsed
          updatedCustomerPointBalance
        }
      }
    `,
    variables: {
      authToken,
      tenantEmployeePin,
      rewardsAndQuantities
    }
  });

  return result.data.redeemPointsForReward;
};

export const recordPurchase = async (
  apollo: ApolloClient<{}>,
  authToken: string,
  tenantEmployeePin: string,
  purchaseAmountDollars: number,
  purchaseDescriptionCategoryIds: string[],
  promoCode: string
): Promise<{
  pointsEarned: number;
}> => {
  const result: any = await apollo.mutate({
    mutation: gql`
      mutation recordPurchase(
        $authToken: String!
        $tenantEmployeePin: String!
        $purchaseAmountDollars: Int!
        $purchaseDescriptionCategoryIds: [ID!]!
        $promoCode: String
      ) {
        recordPurchase(
          authToken: $authToken
          tenantEmployeePin: $tenantEmployeePin
          purchaseAmountDollars: $purchaseAmountDollars
          purchaseDescriptionCategoryIds: $purchaseDescriptionCategoryIds
          promoCode: $promoCode
        ) {
          pointsEarned
        }
      }
    `,
    variables: {
      authToken,
      tenantEmployeePin,
      purchaseAmountDollars,
      purchaseDescriptionCategoryIds,
      promoCode
    }
  });

  return result.data.recordPurchase;
};

export const validateTenantUserPin = async (
  apollo: ApolloClient<{}>,
  pin: string
): Promise<{
  userId: string;
  invalidityCause: string;
}> => {
  const result: any = await apollo.query({
    query: gql`
      query validateTenantUserPin($pin: String!) {
        validateTenantUserPin(pin: $pin) {
          userId
          invalidityCause
        }
      }
    `,
    variables: {
      pin
    },
    fetchPolicy: "network-only"
  });

  return result.data.validateTenantUserPin;
};

export const getPhoneNumberAvailability = async (
  apollo: ApolloClient<{}>,
  phoneNumber: string
): Promise<boolean> => {
  const result: any = await apollo.query({
    query: gql`
      query getPhoneNumberAvailability($phoneNumber: String) {
        getPhoneNumberAvailability(phoneNumber: $phoneNumber)
      }
    `,
    variables: {
      phoneNumber
    },
    fetchPolicy: "network-only"
  });

  return result.data.getPhoneNumberAvailability;
};

export const validatePromoCode = async (
  apollo: ApolloClient<{}>,
  authToken: string,
  code: string
): Promise<any> => {
  const result: any = await apollo.query({
    query: gql`
      query validatePromoCodeForSubjectLocation(
        $authToken: String!
        $code: String!
      ) {
        validatePromoCodeForSubjectLocation(
          authToken: $authToken
          code: $code
        ) {
          name
          details
          status
        }
      }
    `,
    variables: {
      authToken,
      code
    },
    fetchPolicy: "network-only"
  });

  return result.data.validatePromoCodeForSubjectLocation;
};



// WEBPACK FOOTER //
// ./src/utils/apis.ts